/**
 * Fans' email addresses, masked for the creator (Nicky, 22 Oct 2026: "can we
 * simply mask the fan emails?"). A creator sees "j•••@gmail.com", never the
 * whole address; staff (admin, or a recorded view-as session) still see it in
 * full — see useFanEmailDisplay.
 *
 * Rules: the first character of the local part, then "•••", then "@" and the
 * domain (lowercased). A one-character local part shows as "•••@domain".
 * Plus-addressing and anything else after the first character is hidden.
 * Display only: the real address stays in the data, so search, blocking and
 * the server's replies keep working.
 */

export const EMAIL_MASK = '•••';

const looksLikeEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+$/.test(value) || /^[^\s@]+@$/.test(value);

/** "jane.doe+fans@Gmail.com" → "j•••@gmail.com". Not an address → returned as given (trimmed). */
export function maskEmail(value: string | null | undefined): string {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return '';
  const at = raw.lastIndexOf('@');
  if (at < 0) return raw;
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1).toLowerCase();
  if (!local) return `${EMAIL_MASK}@${domain}`;
  const first = Array.from(local)[0];
  const lead = Array.from(local).length > 1 ? first : '';
  return `${lead}${EMAIL_MASK}@${domain}`;
}

/**
 * A name shown next to (or instead of) a masked address. A name that is really
 * an address (email-only fans) is masked; a name that is just the address's
 * local part ("jdoe1987" for jdoe1987@…, which sign-up copies into full_name)
 * would rebuild the whole address beside "j•••@gmail.com", so it shows as the
 * masked address instead. Real names pass through.
 */
export function maskIfEmail(value: string | null | undefined, email?: string | null): string {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (looksLikeEmail(raw)) return maskEmail(raw);
  const addr = typeof email === 'string' ? email.trim() : '';
  const at = addr.lastIndexOf('@');
  if (raw && at > 0) {
    const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const local = addr.slice(0, at);
    if (raw.toLowerCase() === local.toLowerCase() || (squash(raw) && squash(raw) === squash(local))) {
      return maskEmail(addr);
    }
  }
  return raw;
}
